import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Timeline from '../components/Timeline';
import StarBackground from '../components/StarBackground';
import Character from '../components/Character';
import { useContext } from 'react';
import { AppContext } from '../contexts/AppContext';
import { getAssetUrl } from '../api/config';
import { getTranslation } from '../config/translations';
import syllablesConfig from "../exercises/syllables/syllablesConfig";
import nonWordsConfig from "../exercises/nonWords/nonWordsConfig";
import sentencesConfig from "../exercises/sentences/sentencesConfig"; // Import getTranslation

export default function Reward() {
    const { exerciseId, level } = useParams();
    const [headerColor, setHeaderColor] = useState('#ef3c3c');
    const [rewardImage, setRewardImage] = useState('');
    const [rewardAudio, setRewardAudio] = useState('');
    const [character, setCharacter] = useState(null);
    const [planetImage, setPlanetImage] = useState('/assets/images/hiding/Planet_Background_blue.png');
    const { enableText, enableAudio, setEnableText, setEnableAudio } = useContext(AppContext);

    const audioRef = useRef(null);
    const navigate = useNavigate();

    // State for current language and translations
    const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('selectedLanguage') || 'it');
    const translations = getTranslation(currentLanguage);

    // Effect to listen for language changes from localStorage
    useEffect(() => {
        const handleLanguageChange = () => {
            const newLanguage = localStorage.getItem('selectedLanguage') || 'it';
            if (newLanguage !== currentLanguage) {
                setCurrentLanguage(newLanguage);
            }
        };

        const interval = setInterval(handleLanguageChange, 500); // Check for language changes periodically

        return () => clearInterval(interval);
    }, [currentLanguage]); // Re-run this effect if currentLanguage changes

    useEffect(() => {
        // Use the currentLanguage state variable to construct the path
        const language = currentLanguage; // <-- 'language' is derived from currentLanguage state

        // Configure header color and paths for image/audio based on the exercise
        let color = '#ef3c3c';
        let imagePath = '';
        let audioPath = '';
        let exerciseCharacter = 'Mars'; // Default character
        let planet = '/assets/images/hiding/Planet_Background_blue.png'; // Default planet

        if (exerciseId === 'syllables') {
            color = syllablesConfig.headerColor;
            imagePath = `/assets/images/rewards/syllables/${level}.png`;
            audioPath = getAssetUrl(`/audios/rewards/${language}/syllables/${level}.mp3`);
            exerciseCharacter = syllablesConfig.character || 'Mars';
            planet = syllablesConfig.planetBackground || planet;
        } else if (exerciseId === 'nonWords') {
            color = nonWordsConfig.headerColor;
            imagePath = `/assets/images/rewards/nonWords/${level}.png`;
            audioPath = getAssetUrl(`/audios/rewards/${language}/nonWords/${level}.mp3`);
            exerciseCharacter = nonWordsConfig.character || 'Mars';
            planet = nonWordsConfig.planetBackground || planet;
        } else if (exerciseId === 'sentences') {
            color = sentencesConfig.headerColor;
            imagePath = `/assets/images/rewards/sentences/${level}.png`;
            // Dynamic audioPath using the 'language' variable from state
            audioPath = getAssetUrl(`/audios/rewards/sentences/${language}/${level}.mp3`); // <-- language used here
            exerciseCharacter = sentencesConfig.character || 'Mars';
            planet = sentencesConfig.planetBackground || planet;
        }

        setHeaderColor(color);
        setRewardImage(imagePath);
        setRewardAudio(audioPath);
        setCharacter(exerciseCharacter);
        setPlanetImage(planet);
    }, [exerciseId, level, currentLanguage]); // currentLanguage is a dependency

    useEffect(() => {
        // Play the reward audio if enabled and audio path is available
        if (enableAudio && rewardAudio) {
            if (audioRef.current) {
                audioRef.current.pause(); // Stop previous audio if any
                audioRef.current.currentTime = 0;
            }
            const audio = new Audio(rewardAudio);
            audioRef.current = audio;
            audio.play().catch(error => {
                console.log('Audio playback failed:', error);
            });
        }
    }, [rewardAudio, enableAudio]);

    const handleContinue = () => {
        // Stop audio if playing
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }

        // Navigate to the next level or congratulations page
        const currentLevel = parseInt(level);
        const nextLevel = currentLevel + 1;

        // Determine the maximum number of levels for each exercise
        let maxLevels = 14; // default
        if (exerciseId === 'syllables') maxLevels = syllablesConfig.totalLevels || 14;
        else if (exerciseId === 'nonWords') maxLevels = nonWordsConfig.totalLevels || 14;
        else if (exerciseId === 'sentences') maxLevels = sentencesConfig.totalLevels || 14;

        if (nextLevel > maxLevels) {
            navigate(`/exercise/${exerciseId}/congratulations`);
        } else {
            navigate(`/exercise/${exerciseId}/main/${nextLevel}`);
        }
    };

    // Generate array of reward images for sentences exercise
    const sentencesRewardCards = [];
    if (exerciseId === 'sentences') {
        const total = sentencesConfig.totalLevels || 14;
        for (let i = 1; i <= total; i++) {
            sentencesRewardCards.push(`/assets/images/rewards/sentences/${i}.png`);
        }
    }

    return (
        <div style={styles.wrapper}>
            <Header
                color={headerColor}
                enableText={enableText}
                setEnableText={setEnableText}
                enableAudio={enableAudio}
                setEnableAudio={setEnableAudio}
            />
            {/* Timeline for sentences exercise */}
            {exerciseId === 'sentences' && (
                <Timeline current={parseInt(level)} total={sentencesConfig.totalLevels || 14} cards={sentencesRewardCards} />
            )}
            <StarBackground />

            {/* Background planet from config */}
            <img
                src={planetImage}
                alt="Background Planet"
                style={styles.backgroundPlanet}
            />

            <div style={styles.scene}>
                {/* Centered reward image */}
                {rewardImage && (
                    <div style={styles.rewardContainer}>
                        <img
                            src={rewardImage}
                            alt="Reward"
                            style={styles.rewardImage}
                            onError={(e) => {
                                console.log('Failed to load reward image:', rewardImage);
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                )}

                {/* Continue button - Use translated text */}
                <button style={styles.continueButton} onClick={handleContinue}>
                    {translations.continueButton || 'Continua →'}
                </button>
            </div>
            {character && (
                <Character
                    name={character}
                    size={600}
                    position={exerciseId === 'sentences' ? 'reward' : 'right'}
                />
            )}
        </div>
    );
}

const styles = {
    wrapper: {
        height: '100vh',
        color: 'white',
        fontFamily: "'Autour One', cursive",
        overflow: 'hidden',
        position: 'relative',
    },
    scene: {
        position: 'relative',
        height: 'calc(100vh - 100px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // ADDED: This reserves space at the bottom, pushing the content higher
        paddingBottom: '150px',
    },
    backgroundPlanet: {
        position: 'absolute',
        bottom: '-400px',
        width: '100%',
        height: 'auto',
        zIndex: 1,
    },
    rewardContainer: {
        position: 'relative',
        zIndex: 2,
        marginBottom: '40px',
    },
    rewardImage: {
        maxWidth: '400px',
        maxHeight: '400px',
        width: 'auto',
        height: 'auto',
        borderRadius: '20px',
    },
    congratsText: {
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#fff',
        textAlign: 'center',
        marginBottom: '30px',
        zIndex: 3,
        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
    },
    characterContainer: {
        position: 'absolute',
        bottom: '0px',
        right: '5%',
        zIndex: 3,
        transform: 'scale(2)',
        transformOrigin: 'bottom center',
    },
    continueButton: {
        padding: '15px 35px',
        backgroundColor: '#1a3751',
        border: 'none',
        borderRadius: '20px',
        color: 'white',
        fontSize: '24px',
        fontFamily: "'Autour One', cursive",
        cursor: 'pointer',
        zIndex: 3,
        boxShadow: '0 6px 15px rgba(60, 155, 239, 0.4)',
        transition: 'all 0.3s ease',
        // OPTIONAL: You can increase this if you want more space between the button and the invisible bottom barrier
        marginBottom: '20px',
    },
};
